/**
 * AECampaignOversight — AE/Admin internal oversight of a single brand campaign.
 *
 *  - Operational banner (CM/TM names, mandate count, address X/Y, deliverable X/Y, overdue rows)
 *  - Status filter chips + Mandate filter chips
 *  - Creator rows w/ status / address chip / overdue highlight
 *  - Mandates panel (read+write via /api/brand/campaigns/{id}/mandates — AE has access)
 *  - Assign-CM / Assign-TM buttons
 *
 * Read-only on creator state. No brand-facing endpoints called.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, AlertTriangle, Briefcase, UserCog, MapPin, Users, ShieldCheck, Clock, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { MandatesPanel, MandateTag, mandatesForCreator } from '../../components/MandatesPanel';
import { AssignCMTMDialog } from '../../components/ae/AssignCMTMDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { key: 'all',       label: 'All',       match: () => true },
  { key: 'invited',   label: 'Invited',   match: (s) => ['invited', 'pitched', 'outreach'].includes(s) },
  { key: 'responded', label: 'Responded', match: (s) => ['interested', 'accepted', 'rejected', 'declined', 'no_response_yet'].includes(s) },
  { key: 'confirmed', label: 'Confirmed', match: (s) => ['accepted', 'locked', 'approved', 'draft_submitted', 'revision_required'].includes(s) },
  { key: 'live',      label: 'Live',      match: (s) => ['live', 'posted'].includes(s) },
  { key: 'completed', label: 'Completed', match: (s) => s === 'completed' },
];

const STAGE_COLOR = {
  invited: '#71717a', pitched: '#0055FF', outreach: '#9B6EFF',
  interested: '#0055FF', accepted: '#00C972', rejected: '#FF3B30', declined: '#FF3B30',
  no_response_yet: '#52525b', approved_by_brand: '#FFB800',
  product_shipped: '#A855F7', product_delivered: '#EC4899',
  draft_submitted: '#06B6D4', revision_required: '#F97316',
  approved: '#84CC16', posted: '#22C55E',
  locked: '#00C972', live: '#FF451A', completed: '#00C972',
};

const fmtNum = (n) => {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

export const AECampaignOversight = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [mandateFilter, setMandateFilter] = useState('all');
  const [assignDialog, setAssignDialog] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/ae/campaigns/${id}/oversight`, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load oversight');
    } finally {
      setLoading(false);
    }
  };
  const reloadMandates = async () => {
    try {
      const { data: ms } = await axios.get(`${API_URL}/api/brand/campaigns/${id}/mandates`, { withCredentials: true });
      setData((p) => p ? { ...p, mandates: ms || [] } : p);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { if (id) load(); /* eslint-disable-next-line */ }, [id]);

  const rows = data?.creators || [];
  const mandates = data?.mandates || [];
  const ops = data?.ops || {};

  const statusCounts = useMemo(() => {
    const out = { all: rows.length };
    for (const sf of STATUS_FILTERS.slice(1)) out[sf.key] = rows.filter((r) => sf.match(r.status)).length;
    return out;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const sf = STATUS_FILTERS.find((x) => x.key === statusFilter) || STATUS_FILTERS[0];
    let out = rows.filter((r) => sf.match(r.status));
    if (mandateFilter !== 'all') {
      const m = mandates.find((x) => x.mandate_id === mandateFilter);
      if (m && m.assigned_to !== 'all' && Array.isArray(m.assigned_to)) {
        out = out.filter((r) => m.assigned_to.includes(r.row_id));
      }
    }
    return out;
  }, [rows, statusFilter, mandateFilter, mandates]);

  if (loading || !data) return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0B0D]">
      <Sidebar />
      <div className="flex-1 ml-64 p-8 text-[#6B6B6B] dark:text-zinc-500">Loading oversight…</div>
    </div>
  );

  const roster = rows.map((r) => ({ id: r.row_id, name: r.creator_name || '—' }));

  return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0B0D]">
      <Sidebar />
      <div className="flex-1 ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Top bar */}
          <Link to="/ae/campaigns" className="inline-flex items-center gap-1 text-xs text-[#6B6B6B] dark:text-zinc-500 hover:text-white mb-4" data-testid="ae-oversight-back">
            <ArrowLeft size={12} /> All campaigns
          </Link>

          <div className="flex items-start justify-between mb-6 flex-wrap gap-3" data-testid="ae-oversight-header">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={14} className="text-[#FF451A]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] dark:text-zinc-500">{data.brand_name}</span>
                {data.is_overdue && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-[#FF3B30]/15 text-[#FF3B30] inline-flex items-center gap-1">
                    <AlertTriangle size={9} /> OVERDUE
                  </span>
                )}
              </div>
              <h1 className="font-display text-4xl text-[#0F0F0F] dark:text-white tracking-tight">{data.campaign_name}</h1>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-500 mt-1">{data.creator_count} creators · {data.days_since_update ?? 0} day{data.days_since_update === 1 ? '' : 's'} since last update</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setAssignDialog({ kind: 'cm' })} className="bg-white/5 hover:bg-white/10 text-[#0F0F0F] dark:text-white text-xs px-3 py-1.5 rounded-full border border-black/[0.10] dark:border-white/10" data-testid="ae-oversight-assign-cm">
                <Briefcase size={12} className="mr-1" /> Assign CM
              </Button>
              <Button onClick={() => setAssignDialog({ kind: 'tm' })} className="bg-white/5 hover:bg-white/10 text-[#0F0F0F] dark:text-white text-xs px-3 py-1.5 rounded-full border border-black/[0.10] dark:border-white/10" data-testid="ae-oversight-assign-tm">
                <UserCog size={12} className="mr-1" /> Assign TM
              </Button>
            </div>
          </div>

          {/* Ops banner — internal, brand can't see */}
          <div className="bg-white dark:bg-[#0A0B0D] border border-[#FF451A]/20 rounded-xl p-4 mb-6" data-testid="ae-ops-banner">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-[#FF451A] mb-3">
              <ShieldCheck size={11} /> Internal ops · not visible to brand
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <OpsTile label="Campaign Manager" value={data.campaign_managers.map((m) => m.name).join(', ') || '—'} Icon={Briefcase} />
              <OpsTile label="Talent Manager"   value={data.talent_managers.map((m) => m.name).join(', ') || '—'} Icon={UserCog} />
              <OpsTile label="Mandates"         value={String(ops.mandate_count || 0)} Icon={ShieldCheck} />
              <OpsTile label="Addresses"        value={`${ops.address_collected ?? 0} / ${ops.address_total ?? 0}`} Icon={MapPin} />
              <OpsTile label="Drafts Submitted" value={`${ops.deliverable_submitted ?? 0} / ${ops.deliverable_total ?? 0}`} Icon={CheckCircle2} />
            </div>
            {(ops.overdue_rows || 0) > 0 && (
              <div className="mt-3 text-[11px] text-[#FF3B30] inline-flex items-center gap-1" data-testid="ae-ops-overdue-line">
                <AlertTriangle size={11} /> {ops.overdue_rows} creator{ops.overdue_rows === 1 ? '' : 's'} stuck for &gt; 3 days
              </div>
            )}
          </div>

          {/* Status filters */}
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="ae-status-filter-bar">
            {STATUS_FILTERS.map((sf) => {
              const active = statusFilter === sf.key;
              return (
                <button
                  key={sf.key}
                  onClick={() => setStatusFilter(sf.key)}
                  className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${active ? 'border-[#FF451A] bg-[#FF451A]/20 text-[#FF451A]' : 'border-black/[0.10] dark:border-white/10 bg-white/5 text-[#6B6B6B] dark:text-zinc-400 hover:text-white'}`}
                  data-testid={`ae-status-chip-${sf.key}`}
                >
                  {sf.label} <span className="opacity-60">· {statusCounts[sf.key] ?? 0}</span>
                </button>
              );
            })}
          </div>
          {mandates.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="ae-mandate-filter-bar">
              <button
                onClick={() => setMandateFilter('all')}
                className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${mandateFilter === 'all' ? 'border-[#FF451A] bg-[#FF451A]/20 text-[#FF451A]' : 'border-black/[0.10] dark:border-white/10 bg-white/5 text-[#6B6B6B] dark:text-zinc-400 hover:text-white'}`}
              >All mandates · {rows.length}</button>
              {mandates.map((m) => {
                const cnt = m.assigned_to === 'all' ? rows.length : (Array.isArray(m.assigned_to) ? m.assigned_to.length : 0);
                const active = mandateFilter === m.mandate_id;
                return (
                  <button
                    key={m.mandate_id}
                    onClick={() => setMandateFilter(m.mandate_id)}
                    className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${active ? 'border-[#FF451A] bg-[#FF451A]/20 text-[#FF451A]' : 'border-black/[0.10] dark:border-white/10 bg-white/5 text-[#6B6B6B] dark:text-zinc-400 hover:text-white'}`}
                    data-testid={`ae-mandate-chip-${m.mandate_id}`}
                    title={m.title}
                  >
                    {m.title.length > 22 ? `${m.title.slice(0, 22)}…` : m.title} <span className="opacity-60">· {cnt}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Creator rows */}
          <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono mb-2">Showing {filteredRows.length} of {rows.length}</p>
          {filteredRows.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-8 text-center text-sm text-[#6B6B6B] dark:text-zinc-500" data-testid="ae-rows-empty">
              <Users className="mx-auto mb-2 text-[#A1A1AA] dark:text-zinc-600" size={28} />
              No creators match the selected filters.
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredRows.map((r) => {
                const stageColor = STAGE_COLOR[r.status] || '#999';
                const collected = r.address_status === 'collected';
                const rowMandates = mandatesForCreator(mandates, r.row_id);
                return (
                  <div
                    key={r.row_id}
                    className={`bg-white dark:bg-[#0A0B0D] border rounded-xl p-3 flex items-center gap-3 flex-wrap ${r.is_overdue ? 'border-[#FF3B30]/40 ring-1 ring-[#FF3B30]/10' : 'border-black/[0.08] dark:border-white/5'}`}
                    data-testid={r.is_overdue ? 'ae-row-overdue' : 'ae-row'}
                  >
                    {r.profile_photo_url ? (
                      <img src={r.profile_photo_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-xs font-bold shrink-0">
                        {r.creator_name?.[0]?.toUpperCase() || '·'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0F0F0F] dark:text-white truncate">{r.creator_name}</div>
                      <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
                        {r.instagram_handle && <span>@{r.instagram_handle}</span>}
                        {r.instagram_followers > 0 && <span>· {fmtNum(r.instagram_followers)} followers</span>}
                      </div>
                      {rowMandates.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {rowMandates.map((m) => <MandateTag key={m.mandate_id} mandate={m} />)}
                        </div>
                      )}
                    </div>
                    {r.is_overdue && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider font-mono shrink-0 bg-[#FF3B30]/15 text-[#FF3B30] inline-flex items-center gap-1">
                        <Clock size={9} /> Overdue
                      </span>
                    )}
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider font-mono shrink-0"
                      style={{ backgroundColor: `${stageColor}15`, color: stageColor }}
                    >
                      {r.status.replace(/_/g, ' ')}
                    </span>
                    {r.source === 'bulk' && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider font-mono shrink-0 inline-flex items-center gap-1 border ${collected ? 'bg-[#00C972]/15 text-[#00C972] border-[#00C972]/30' : 'bg-[#FFB800]/15 text-[#FFB800] border-[#FFB800]/30'}`}
                      >
                        <MapPin size={9} /> {collected ? 'Address Collected' : 'Address Pending'}
                      </span>
                    )}
                    {r.last_updated && (
                      <span className="text-[10px] text-[#A1A1AA] dark:text-zinc-600 font-mono shrink-0">{String(r.last_updated).slice(0, 10)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Mandates panel */}
          <div className="pt-6 mt-6 border-t border-black/[0.08] dark:border-white/5">
            <MandatesPanel
              campaignId={id}
              mandates={mandates}
              onChange={reloadMandates}
              roster={roster}
            />
          </div>
        </div>
      </div>

      {assignDialog && (
        <AssignCMTMDialog
          campaign={{ id, campaign_name: data.campaign_name }}
          kind={assignDialog.kind}
          onClose={() => setAssignDialog(null)}
          onAssigned={load}
        />
      )}
    </div>
  );
};

const OpsTile = ({ label, value, Icon }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 inline-flex items-center gap-1">
      <Icon size={9} /> {label}
    </div>
    <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate">{value}</div>
  </div>
);
